import { useContext, useEffect, useMemo, useState } from "react";
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from "formik";
import * as Yup from "yup";
import OwLabel from "../../OwLabel/OwLabel";
import OwSelectOption from "../../OwSelectOption/OwSelectOption";
import { OwFnInput } from "../../OwFnInput/OwFnInput";
import moment from "moment/moment";
import OwToggle from "../../OwToggle/OwToggle";
import creditCardType from "credit-card-type";
import IOrder_Progress_State from "../../../../models/IOrder_Progress_State";
import { OwIconTooltip } from "../../OwIconTooltip/OwIconTooltip";
import OwButton from "../../OwButton/OwButton";
import IUiSocketSetup from "../../../../models/IUiSocketSetup";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IOnGetCardDTO from "../../../../models/DTO/IOnGetCardDTO";
import IOnCreateCardFromTokenDTO from "../../../../models/DTO/IOnCreateCardFromTokenDTO";
import { SocketContext } from "../../../../contexts/SocketContext";
import IGetCardDTO from "../../../../models/DTO/IGetCardDTO";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import UiContext from "../../../../contexts/UiContext";
import IQBPaymentCard from "../../../../models/IQBPaymentCard";
import IUpdateOrderCardToUseDTO from "../../../../models/DTO/IUpdateOrderCardToUseDTO";
import IOrder from "../../../../models/IOrder";
import QuickbooksPaymentsService from "../../../../services/QuickbooksPaymentsService";
import IQBPaymentsDetails from "../../../../models/IQBPaymentsDetails";
import QBPaymentsCardTypeEnum from "../../../../models/QBPaymentsCardTypeEnum";
import INotification from "../../../../models/INotification";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import ICreateCardFromTokenDTO from "../../../../models/DTO/ICreateCardFromTokenDTO";
import { QbPaymentContext } from "../../../../contexts/QbPaymentContext";

type Props = {
    nextStep?: IOrder_Progress_State;
    orderModel: IOrder;
};

// Form values interface
interface CardFormValues {
    card: string;
    expMonth: string;
    expYear: string;
    cvc: string;
    nameOnCard: string;
    billingPostalCode: string;
    saveCard: boolean;
}

/**
 * The New Card Component
 *
 * The sequence for a new card is:
 * 1. User enters card details
 * 2. User clicks submit
 *
 * 3. The card details are sent to the QBO API
 * 4. The QBO API returns a token
 *
 * 5. The token is sent to the server
 * 6. The server creates the card in the QBO API
 * 7. The server returns the card id
 *
 * 8. The card id is sent to the QBO API
 * 9. The QBO API returns a card
 *
 * 10. The card is sent to the server
 * 11. The server updates the order card to use
 *
 * 12. The user is redirected to the next step
 *
 * @param props
 * @constructor
 */
export const QuickBooksPaymentNewCard = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);
    const socket = useContext(SocketContext)!;
    const qbPaymentContent = useContext(QbPaymentContext);

    const onGetCard = (data: IOnGetCardDTO) => {
        if (data.card) {
            updateOrderCardToUse(data.card);
            qbPaymentContent.setExistingCard(data.card);
            qbPaymentContent.nextSlide();
        }
    };

    const onSetupCardFromToken = (data: IOnCreateCardFromTokenDTO) => {
        if (!saveCard) {
            //     createCharge(data.card_id, true);
        } else {
            getCard(data.card_id);
        }
    };

    // Validation schema
    const validationSchema = Yup.object({
        card: Yup.string()
            .required("Card number is required")
            .matches(/^[0-9]{15,16}$/, "Card number must be 15-16 digits"),
        expMonth: Yup.string().required("Month is required"),
        expYear: Yup.string().required("Year is required"),
        cvc: Yup.string()
            .required("CVC is required")
            .matches(/^[0-9]{3,4}$/, "CVC must be 3-4 digits"),
        nameOnCard: Yup.string().required("Name on card is required"),
        billingPostalCode: Yup.string().required("Postal code is required"),
    });

    // Initial values
    const initialValues: CardFormValues = {
        card: "",
        expMonth: "",
        expYear: "",
        cvc: "",
        nameOnCard: "",
        billingPostalCode: "",
        saveCard: !!props.nextStep?.onlyCollectCcToUse,
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.PaymentsOnCreateCardFromToken, fn: onSetupCardFromToken },
        { endpoint: EndpointsEnum.PaymentsOnGetCard, fn: onGetCard },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const getCard = (cardId: string) => {
        const payload: IGetCardDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: props.orderModel.Vendor!.guid,
            cardId,
        };

        socket.emit(EndpointsEnum.PaymentsGetCard, payload);
    };

    const updateOrderCardToUse = (existingCard: IQBPaymentCard) => {
        const payload: IUpdateOrderCardToUseDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: props.orderModel.Vendor!.guid,
            orderGuid: props.orderModel.guid,
            qbo_CardToUseDetail: existingCard,
        };

        socket.emit(EndpointsEnum.OrderUpdateOrderCardToUse, payload);

        qbPaymentContent.setIsLoading(true);
    };

    const monthOptions = useMemo(() => {
        return Array.from({ length: 12 }, (_, i) => ({
            value: (i + 1).toString().padStart(2, "0"),
            title: (i + 1).toString().padStart(2, "0"),
        }));
    }, []);

    const yearOptions = useMemo(() => {
        const currentYear = moment().year();
        return Array.from({ length: 21 }, (_, i) => ({
            value: (currentYear + i).toString(), // Adding i to current year
        }));
    }, []);

    const [saveCard, setSaveCard] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);

    const setupCardFromToken = (cardToken: string) => {
        const payload: ICreateCardFromTokenDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: props.orderModel.Vendor!.guid,
            cardToken,
        };

        socket.emit(EndpointsEnum.PaymentsCreateCardFromToken, payload);

        qbPaymentContent.setIsLoading(true);
    };

    const checkCardType = (cardNumber: string) => {
        if (cardNumber.length > 4) {
            const cardTypeDetail = creditCardType(cardNumber);

            if (cardTypeDetail.length) {
                const firstCardType = cardTypeDetail[0].type;

                return {
                    visa: firstCardType === "visa",
                    mc: firstCardType === "mastercard",
                    amex: firstCardType === "american-express",
                    disc: firstCardType === "discover",
                    diners: firstCardType === "diners-club",
                    jcb: firstCardType === "jcb",
                };
            }
        }

        return {
            visa: false,
            mc: false,
            amex: false,
            disc: false,
            diners: false,
            jcb: false,
        };
    };

    const createCardToken = async (card: {
        type: QBPaymentsCardTypeEnum;
        number: string;
        name: string;
        expYear: string;
        expMonth: string;
        cvc: string;
        billingPostalCode: number;
    }) => {
        const details: IQBPaymentsDetails = {
            card: {
                cardType: card.type,
                number: card.number.replace(/\s/g, "").replace(/_/g, ""),
                name: card.name,
                expYear: card.expYear,
                expMonth: card.expMonth,
                cvc: card.cvc,
                address: {
                    postalCode: card.billingPostalCode,
                },
            },
        };

        return await QuickbooksPaymentsService.createAToken("card", details);
    };

    const getCardType = (card: string): QBPaymentsCardTypeEnum => {
        const ccSelected = checkCardType(card);

        let cardType: QBPaymentsCardTypeEnum = QBPaymentsCardTypeEnum.VISA;

        if (ccSelected.mc) {
            cardType = QBPaymentsCardTypeEnum.MC;
        } else if (ccSelected.amex) {
            cardType = QBPaymentsCardTypeEnum.AMEX;
        } else if (ccSelected.disc) {
            cardType = QBPaymentsCardTypeEnum.DISC;
        } else if (ccSelected.jcb) {
            cardType = QBPaymentsCardTypeEnum.JCB;
        } else if (ccSelected.diners) {
            cardType = QBPaymentsCardTypeEnum.DINERS;
        } else {
            cardType = QBPaymentsCardTypeEnum.VISA;
        }

        return cardType;
    };

    const handleSubmit = async (values: CardFormValues, { setSubmitting }: FormikHelpers<CardFormValues>) => {
        const cardType = getCardType(values.card);

        // put the values in the context provider.
        const cardToken = await createCardToken({
            type: cardType,
            number: values.card,
            name: values.nameOnCard,
            expYear: values.expYear,
            expMonth: values.expMonth,
            cvc: values.cvc,
            billingPostalCode: values.billingPostalCode,
        });

        setSaveCard(values.saveCard);

        if (values.saveCard) {
            setupCardFromToken(cardToken);
        } else {
            // do a setup for not saving the card and just charging it?
        }
        // Here you would handle the submission of the form
        // For example, you might want to call a function to process the payment
        console.log("Form submitted with values:", values);
        // Implement payment processing logic here
        setSubmitting(false);
    };

    return (
        <div className={"QuickBooksPaymentNewCard"}>
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                {({ values, setFieldValue, isSubmitting, errors, touched, isValid }) => {
                    const ccSelected = checkCardType(values.card);

                    return (
                        <Form>
                            <div>
                                <div>
                                    <OwLabel title={"card"} />
                                    <Field
                                        as={OwFnInput}
                                        name="card"
                                        fn={(e: any) => setFieldValue("card", e.target.value)}
                                        value={values.card}
                                        autoComplete="cc-number"
                                    />
                                    <ErrorMessage name="card" component="div" className="text-danger" />
                                </div>

                                <div>
                                    <i className={`fa-2x me-2 fab fa-cc-visa ${ccSelected.visa ? "text-primary" : ""}`} />
                                    <i className={`fa-2x me-2 fab fa-cc-mastercard ${ccSelected.mc ? "text-primary" : ""}`} />
                                    <i className={`fa-2x me-2 fab fa-cc-amex ${ccSelected.amex ? "text-primary" : ""}`} />
                                    <i className={`fa-2x me-2 fab fa-cc-discover ${ccSelected.disc ? "text-primary" : ""}`} />
                                </div>

                                <div className="mt-3">
                                    <OwLabel title={"exp_date"} />
                                    <div className={"d-flex"}>
                                        <div className={"w-5r me-2"}>
                                            <Field
                                                as={OwSelectOption}
                                                name="expMonth"
                                                options={monthOptions}
                                                titleProp={"title"}
                                                valueProp={"value"}
                                                selectedOption={values.expMonth}
                                                hasSelectedOptionProp={false}
                                                fn={(e: any) => setFieldValue("expMonth", e.target.value)}
                                            />
                                            <ErrorMessage name="expMonth" component="div" className="text-danger" />
                                        </div>
                                        <div className={"w-5r"}>
                                            <Field
                                                as={OwSelectOption}
                                                name="expYear"
                                                options={yearOptions}
                                                titleProp={"value"}
                                                valueProp={"value"}
                                                selectedOption={values.expYear}
                                                hasSelectedOptionProp={false}
                                                fn={(e: any) => setFieldValue("expYear", e.target.value)}
                                            />
                                            <ErrorMessage name="expYear" component="div" className="text-danger" />
                                        </div>
                                    </div>
                                </div>

                                <div className={"mt-3"}>
                                    <OwLabel title={"cvc"} />
                                    <div className={"w-6r"}>
                                        <Field
                                            as={OwFnInput}
                                            enableDebounce={true}
                                            name="cvc"
                                            fn={(e: any) => setFieldValue("cvc", e.target.value)}
                                            value={values.cvc}
                                            type={"number"}
                                        />
                                        <ErrorMessage name="cvc" component="div" className="text-danger" />
                                    </div>
                                </div>

                                <div className={"mt-3"}>
                                    <OwLabel title={"name_on_card"} />
                                    <Field
                                        as={OwFnInput}
                                        name="nameOnCard"
                                        fn={(e: any) => setFieldValue("nameOnCard", e.target.value)}
                                        value={values.nameOnCard}
                                    />
                                    <ErrorMessage name="nameOnCard" component="div" className="text-danger" />
                                </div>

                                <div className={"mt-3"}>
                                    <OwLabel title={"billing_postal_code"} />
                                    <Field
                                        as={OwFnInput}
                                        name="billingPostalCode"
                                        fn={(e: any) => setFieldValue("billingPostalCode", e.target.value)}
                                        value={values.billingPostalCode}
                                    />
                                    <ErrorMessage name="billingPostalCode" component="div" className="text-danger" />
                                </div>
                            </div>

                            <div className={"mt-3"}>
                                <div className={"d-flex align-items-center position-relative"}>
                                    <Field
                                        as={OwToggle}
                                        fn={(e: any) => setFieldValue("saveCard", !values.saveCard)}
                                        name="saveCard"
                                        label="save_card"
                                        active={values.saveCard}
                                        labelOnRight={true}
                                        disabled={!!props.nextStep?.onlyCollectCcToUse}
                                    />
                                    {props.nextStep?.onlyCollectCcToUse ? (
                                        <OwIconTooltip
                                            iconClassName={"ms-2 fas fa-exclamation-triangle text-warning"}
                                            tooltipContent={"Workflow Config Requires Save"}
                                            inModal={true}
                                        />
                                    ) : null}
                                </div>
                            </div>

                            <div className="mt-3 mb-3">
                                <OwButton
                                    title={"continue"}
                                    fn={() => {}}
                                    type="submit"
                                    disabled={isSubmitting || !isValid || qbPaymentContent.isLoading}
                                />
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};
